
import Vue from 'vue';
import { Component, Prop, Watch } from 'vue-property-decorator';
import { MeasurementSpecification, MediaApiControllerUIClient, TeMeasurement, TestEquipmentTest } from '../../backend/backendapi';
import CenterOnParent from '../../util/CenterOnParent';
import MeasurementSpecificationsTemplateBadges from '../measurementSpecificationTemplates/MeasurementSpecificationsTemplateBadges';
import { initClient } from '../../../api';
import { unique } from '../../../util';
import TestEquipmentMeasurementRow, { type Attachment } from './TestEquipmentMeasurementRow';

type Row = { specification: MeasurementSpecification, measurement: TeMeasurement, attachments: Attachment[] };

@Component({
  components: {
    CenterOnParent,
    MeasurementSpecificationsTemplateBadges,
    TestEquipmentMeasurementRow,
  },
})
export default class TestEquipmentMeasurements extends Vue {
  @Prop({ required: true })
  readonly test!: TestEquipmentTest;

  private readonly mediaApi = initClient(MediaApiControllerUIClient);
  attachments: Attachment[] | null = null;

  get allowSeparateTesterInAnySpecification(): boolean {
    return this.rows.some(r => r.specification.allowSeparateTester);
  }

  get rows(): Row[] {
    this.test.measurements ??= [];
    const measurementsBySpecificationId = new Map(this.test.measurements.map(e => [e.specificationId, e]));
    const attachmentsBySpecificationId = new Map((this.test.measurementSpecifications ?? [])
        .map(e => [
          e.id,
          this.attachments?.filter(m => e.mediaIds?.includes(m.mediaId!)) ?? [],
        ]));

    //create missing measurement objects 
    this.test.measurementSpecifications?.forEach(spec => {
      if (!measurementsBySpecificationId.has(spec.id)) {
        const m = TeMeasurement.fromJS({ specificationId: spec.id });
        this.test.measurements!.push(m);
        measurementsBySpecificationId.set(m.specificationId, m);
      }
    });

    //
    return (this.test.measurementSpecifications ?? [])
        .map(e => ({ specification: e, measurement: measurementsBySpecificationId.get(e.id)!, attachments: attachmentsBySpecificationId.get(e.id)! }));
  }

  async reloadMedia(): Promise<void> {
    this.attachments = null; //reset in case the following code fails to load the media
    const mediaIds = unique(this.test.measurementSpecifications?.flatMap(e => e.mediaIds ?? []) ?? []);
    this.attachments = await Promise.all(mediaIds.map(mediaId =>
        this.mediaApi.get(mediaId)
            .then(media => ({ mediaId: mediaId, media: media } as Attachment))
            .catch(e => ({ mediaId: mediaId, media: null } as Attachment)),
    ));
  }

  @Watch('test.measurementSpecifications', { immediate: true })
  private onTemplateIdChanged(): void {
    this.reloadMedia();
  }
}
